import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ThumbsUp, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Sugestoes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("em_votacao");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", area: "legislativo", description: "" });

  const load = () => api.get("/suggestions", { params: { status: tab } }).then(r => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */}, [tab]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/suggestions", form);
      toast.success("Sugestão enviada!");
      setOpen(false);
      setForm({ title: "", area: "legislativo", description: "" });
      setTab("em_votacao");
      load();
    } catch (err) {
      toast.error("Erro ao enviar sugestão");
    }
  };

  const vote = async (s) => {
    try {
      await api.post(`/suggestions/${s.id}/vote`);
      toast.success("Voto registrado!");
      load();
    } catch { toast.error("Erro ao votar"); }
  };

  return (
    <div className="space-y-4" data-testid="sugestoes-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Sugerir cursos</h1>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="sugestao-new"><Plus className="w-4 h-4 mr-1" /> Nova sugestão</Button>
      </div>
      <p className="text-sm text-muted-foreground">Ajude a construir os próximos cursos do CEAP. Sugira temas e vote nos seus favoritos.</p>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="em_votacao">Em votação</TabsTrigger>
          <TabsTrigger value="aprovado">Aprovados</TabsTrigger>
          <TabsTrigger value="lancado">Lançados</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="space-y-3 mt-4">
          {items.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum item nesta categoria.</p>
            : items.map(s => (
              <Card key={s.id} className="rounded-xl">
                <CardContent className="p-4 flex items-start gap-3">
                  <Button variant="outline" size="icon" onClick={() => vote(s)} disabled={(s.voters || []).includes(user?.id)} data-testid={`vote-${s.id}`}>
                    <ThumbsUp className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="capitalize">{s.area}</Badge>
                      <Badge variant="outline">{s.votes} {s.votes === 1 ? "voto" : "votos"}</Badge>
                    </div>
                    <h3 className="font-display font-semibold text-sm text-[hsl(var(--primary))]">{s.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">por {s.user_name || "Aluno CEAP"}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[460px] rounded-2xl">
          <DialogHeader><DialogTitle>Nova sugestão de curso</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Título do curso</Label>
              <Input required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Como criar um Plano Diretor moderno" data-testid="sugestao-title" /></div>
            <div><Label>Área</Label>
              <Select value={form.area} onValueChange={(v) => setForm(f => ({ ...f, area: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="legislativo">Legislativo</SelectItem>
                  <SelectItem value="licitacoes">Licitações</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Descrição e dificuldades atuais</Label>
              <Textarea rows={4} required value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Conte por que este tema é relevante…" data-testid="sugestao-description" /></div>
            <Button type="submit" className="w-full" data-testid="sugestao-submit">Enviar sugestão</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
